import { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { FaRobot, FaPlus, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import aiCapabilitiesApi from '../../../services/aiCapabilitiesApi';
import { ChartCard, EmptyState, doughnutOptions } from '../analytics/analyticsShared';

const PROVIDER_LABELS = {
  third_party: 'Third-party AI',
  proprietary: 'Proprietary',
  hybrid: 'Hybrid (proprietary + third-party)',
};

const STATUS_LABELS = {
  operational: 'Operational',
  planned: 'Planned',
  deprecated: 'Deprecated',
};

const STATUS_COLORS = {
  operational: 'bg-green-50 text-green-700 ring-green-200',
  planned: 'bg-amber-50 text-amber-700 ring-amber-200',
  deprecated: 'bg-gray-50 text-gray-500 ring-gray-200',
};

const EMPTY_FORM = {
  name: '',
  provider: 'proprietary',
  providerDetail: '',
  description: '',
  inputData: '',
  outputDescription: '',
  status: 'operational',
  codeReference: '',
  usageEventType: '',
};

// An honest, admin-maintained record of what AI capabilities actually exist
// in the platform — which are third-party, which are proprietary, and
// their real operational status. This is meant to be kept accurate against
// the codebase, not aspirational.
function AiCapabilitiesRegistry() {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // capability object or 'new'
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const res = await aiCapabilitiesApi.list();
      setCapabilities(res.capabilities || []);
    } catch (err) {
      /* interceptor toasts */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const providerChartData = useMemo(() => {
    const counts = { third_party: 0, proprietary: 0, hybrid: 0 };
    capabilities.forEach((c) => { if (counts[c.provider] != null) counts[c.provider]++; });
    const colors = { third_party: '#5B7FBF', proprietary: '#1e5c33', hybrid: '#97BC62' };
    const entries = Object.entries(counts).filter(([, v]) => v > 0);
    return {
      labels: (entries.length ? entries : Object.entries(counts)).map(([k]) => PROVIDER_LABELS[k]),
      datasets: [{ data: (entries.length ? entries : Object.entries(counts)).map(([, v]) => v), backgroundColor: (entries.length ? entries : Object.entries(counts)).map(([k]) => colors[k]), borderWidth: 0 }],
    };
  }, [capabilities]);

  const statusChartData = useMemo(() => {
    const counts = { operational: 0, planned: 0, deprecated: 0 };
    capabilities.forEach((c) => { if (counts[c.status] != null) counts[c.status]++; });
    const colors = { operational: '#1e5c33', planned: '#d97706', deprecated: '#6b7280' };
    const entries = Object.entries(counts).filter(([, v]) => v > 0);
    return {
      labels: (entries.length ? entries : Object.entries(counts)).map(([k]) => STATUS_LABELS[k]),
      datasets: [{ data: (entries.length ? entries : Object.entries(counts)).map(([, v]) => v), backgroundColor: (entries.length ? entries : Object.entries(counts)).map(([k]) => colors[k]), borderWidth: 0 }],
    };
  }, [capabilities]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing('new');
  };

  const openEdit = (cap) => {
    setForm({ ...EMPTY_FORM, ...cap });
    setEditing(cap._id);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing === 'new') {
        await aiCapabilitiesApi.create(form);
      } else {
        await aiCapabilitiesApi.update(editing, form);
      }
      closeForm();
      load();
    } catch (err) {
      /* interceptor toasts */
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this AI capability from the registry?')) return;
    try {
      await aiCapabilitiesApi.remove(id);
      load();
    } catch (err) {
      /* interceptor toasts */
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading registry…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">AI Capabilities Registry</h1>
          <p className="text-xs text-gray-400 mt-1">
            An accurate record of what AI actually exists in the platform today — provider, data used, and operational status.
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90"
        >
          <FaPlus /> Add capability
        </button>
      </div>

      {capabilities.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="AI by provider type" className="h-64">
            <div className="h-48">
              <Doughnut data={providerChartData} options={doughnutOptions} />
            </div>
          </ChartCard>
          <ChartCard title="AI by operational status" className="h-64">
            <div className="h-48">
              <Doughnut data={statusChartData} options={doughnutOptions} />
            </div>
          </ChartCard>
        </div>
      )}

      {editing && (
        <ChartCard title={editing === 'new' ? 'Add AI capability' : 'Edit AI capability'}>
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                placeholder="e.g. AI Poultry Health Report"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Provider type</label>
              <select
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              >
                {Object.entries(PROVIDER_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Provider detail</label>
              <input
                value={form.providerDetail}
                onChange={(e) => setForm({ ...form, providerDetail: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                placeholder="e.g. OpenAI gpt-4o-mini"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Description</label>
              <textarea
                required
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Input data</label>
              <input
                value={form.inputData}
                onChange={(e) => setForm({ ...form, inputData: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Output</label>
              <input
                value={form.outputDescription}
                onChange={(e) => setForm({ ...form, outputDescription: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              >
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Code reference</label>
              <input
                value={form.codeReference}
                onChange={(e) => setForm({ ...form, codeReference: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                placeholder="e.g. services/monitoring/healthAnalysis.js"
              />
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button
                type="button"
                onClick={closeForm}
                className="text-sm px-3 py-2 rounded-lg border border-gray-200 flex items-center gap-1"
              >
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="text-sm px-3 py-2 rounded-lg bg-primary text-white">
                Save
              </button>
            </div>
          </form>
        </ChartCard>
      )}

      {capabilities.length === 0 ? (
        <EmptyState
          icon={<FaRobot />}
          title="No AI capabilities documented yet"
          message="Add each real AI feature here so there's a single accurate, checkable record of what's operational, planned, third-party, or proprietary."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {capabilities.map((cap) => (
            <ChartCard key={cap._id} title={cap.name}>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                    {PROVIDER_LABELS[cap.provider]}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${STATUS_COLORS[cap.status]}`}>
                    {STATUS_LABELS[cap.status]}
                  </span>
                </div>
                {cap.providerDetail && <div className="text-xs text-gray-500">{cap.providerDetail}</div>}
                <p className="text-gray-600">{cap.description}</p>
                {cap.inputData && <div className="text-xs text-gray-400"><strong>Input:</strong> {cap.inputData}</div>}
                {cap.outputDescription && <div className="text-xs text-gray-400"><strong>Output:</strong> {cap.outputDescription}</div>}
                {cap.codeReference && (
                  <div className="text-xs text-gray-400 font-mono bg-gray-50 rounded px-2 py-1 inline-block">
                    {cap.codeReference}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => openEdit(cap)} className="text-xs text-primary flex items-center gap-1 hover:opacity-80">
                    <FaEdit /> Edit
                  </button>
                  <button onClick={() => handleDelete(cap._id)} className="text-xs text-red-500 flex items-center gap-1 hover:opacity-80">
                    <FaTrash /> Remove
                  </button>
                </div>
              </div>
            </ChartCard>
          ))}
        </div>
      )}
    </div>
  );
}

export default AiCapabilitiesRegistry;
